import { skuVariationLabel } from '../../../modules/products/public';

export const newClientId = () =>
  typeof crypto !== 'undefined' && crypto.randomUUID
    ? crypto.randomUUID()
    : `tmp-${Date.now()}-${Math.random().toString(36).slice(2, 9)}`;

export const filterMediasByKey = (allMedias, metadataKey, metadataValue) =>
  (allMedias || [])
    .map((media) => {
      const matchingMeta = (media.metadata || []).find((meta) => {
        if (meta.key !== metadataKey) return false;
        const v = meta.value;
        if (typeof v === 'object' && v !== null) return v.id === metadataValue;
        return v === metadataValue;
      });
      if (!matchingMeta) return null;
      return { ...media, metadataJSON: matchingMeta.value };
    })
    .filter(Boolean);

export const buildProductMediaEntries = (allMedias, productId) =>
  filterMediasByKey(allMedias, 'product_image', productId).map((media, index) => ({
    id: newClientId(),
    media_id: media.id,
    source: 'PRODUCT',
    display_order: index,
    file_path: media.file_path,
    mime_type: media.mime_type,
  }));

export const emptyComposition = () => ({ items: [], kits: [] });

const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:8080';

export const mediaFileUrl = (filePath) => {
  if (!filePath) return '';
  return `${API_URL}${filePath}`;
};

export const parseCatalogDetail = (res) => {
  const data = res?.data || res;
  const catalog = data?.catalog || data;
  const composition = data?.composition || emptyComposition();
  return {
    catalog: {
      id: catalog?.id,
      name: catalog?.name ?? '',
      subtitle: catalog?.subtitle ?? '',
      hero_tags: normalizeHeroTags(Array.isArray(catalog?.hero_tags) ? catalog.hero_tags : []),
      promo_blocks: normalizePromoBlocks(Array.isArray(catalog?.promo_blocks) ? catalog.promo_blocks : []),
      section_title: catalog?.section_title ?? '',
      section_subtitle: catalog?.section_subtitle ?? '',
      layout_id: catalog?.layout_id ?? '',
      status: (catalog?.status || 'DRAFT').toUpperCase(),
      order_type_id: catalog?.order_type_id ?? '',
    },
    composition: {
      items: Array.isArray(composition.items) ? composition.items : [],
      kits: Array.isArray(composition.kits) ? composition.kits : [],
    },
  };
};

export const productItemsFromComposition = (composition) =>
  (composition?.items || [])
    .filter((it) => (it.item_type || 'PRODUCT').toUpperCase() !== 'KIT')
    .map(normalizeCatalogProductItem);

export const resolvedSkuDisplayLabel = (sku) => skuVariationLabel(sku);

export const normalizeCatalogProductItem = (item) => ({
  ...item,
  resolved_skus: (item.resolved_skus || []).map((rs) => ({
    ...rs,
    label: rs.label || skuVariationLabel(rs),
  })),
  skus: (item.skus || []).map((sk) => ({
    ...sk,
    _label: sk._label || resolvedSkuDisplayLabel(
      (item.resolved_skus || []).find((rs) => rs.id === sk.sku_id) || { id: sk.sku_id },
    ),
  })),
});

export const kitItemsFromComposition = (composition) => {
  const kits = composition?.kits || [];
  const items = composition?.items || [];
  return kits.map((kit) => {
    const item = items.find((it) => it.id === kit.catalog_item_id) || {};
    return {
      ...kit,
      display_name: item.display_name || '',
      display_description: item.display_description || '',
      badge_label: item.badge_label || '',
      medias: Array.isArray(item.medias) ? item.medias : [],
      is_available: item.is_available !== false,
      display_order: item.display_order ?? 0,
    };
  });
};

export const buildCompositionPayload = (productItems, kits) => {
  const kitItemIds = kits.map((kit, idx) => kit.catalog_item_id || kit._itemId || newClientId());

  const kitCatalogItems = kits.map((kit, idx) => ({
    id: kitItemIds[idx],
    item_type: 'KIT',
    product_id: '',
    display_name: kit.display_name || '',
    display_description: kit.display_description || '',
    badge_label: kit.badge_label || '',
    display_order: kit.display_order ?? 1000 + idx,
    medias: (kit.medias || []).map((m, mIdx) => ({
      id: m.id || newClientId(),
      media_id: m.media_id,
      source: m.source || 'CATALOG',
      display_order: m.display_order ?? mIdx,
    })),
    is_featured: false,
    is_available: kit.is_available !== false,
    sku_selection_mode: 'ALL_ACTIVE',
    skus: [],
  }));

  const items = [
    ...productItems.map((it, idx) => ({
      id: it.id || newClientId(),
      item_type: 'PRODUCT',
      product_id: it.product_id || '',
      display_name: it.display_name || '',
      display_description: it.display_description || '',
      badge_label: it.badge_label || '',
      display_order: it.display_order ?? idx,
      medias: (it.medias || []).map((m, mIdx) => ({
        id: m.id || newClientId(),
        media_id: m.media_id,
        source: m.source || 'PRODUCT',
        display_order: m.display_order ?? mIdx,
      })),
      is_featured: !!it.is_featured,
      is_available: it.is_available !== false,
      sku_selection_mode: it.sku_selection_mode || 'ALL_ACTIVE',
      price_display_mode: it.price_display_mode || 'SHOW',
      skus: (it.sku_selection_mode === 'SELECTED' ? it.skus || [] : []).map((sk, skIdx) => ({
        id: sk.id || newClientId(),
        sku_id: sk.sku_id,
        price_mode: sk.price_mode || 'INHERIT',
        override_price: sk.override_price ?? null,
        min_price: sk.min_price ?? null,
        max_price: sk.max_price ?? null,
        display_order: sk.display_order ?? skIdx,
        is_available: sk.is_available !== false,
      })),
    })),
    ...kitCatalogItems,
  ];

  const kitsPayload = kits.map((kit, kitIdx) => ({
    id: kit.id || newClientId(),
    catalog_item_id: kitItemIds[kitIdx],
    display_name: kit.display_name || '',
    display_description: kit.display_description || '',
    pricing_mode: kit.pricing_mode || 'SUM',
    fixed_price: kit.fixed_price ?? null,
    min_price: kit.min_price ?? null,
    max_price: kit.max_price ?? null,
    items: (kit.items || []).map((line, lineIdx) => ({
      id: line.id || newClientId(),
      catalog_item_id: line.catalog_item_id || '',
      sku_id: line.sku_id || '',
      product_id: line.product_id || '',
      quantity_mode: line.quantity_mode || 'FIXED',
      quantity: Number(line.quantity) || 1,
      min_quantity: line.min_quantity ?? null,
      max_quantity: line.max_quantity ?? null,
      is_required: line.is_required !== false,
      display_order: line.display_order ?? lineIdx,
    })),
  }));

  return { items, kits: kitsPayload };
};

export const createProductItemFromProduct = (product, activeSkus, displayOrder) => ({
  id: newClientId(),
  item_type: 'PRODUCT',
  product_id: product.id,
  display_name: product.name || '',
  display_description: product.description || '',
  display_order: displayOrder,
  is_featured: false,
  is_available: true,
  sku_selection_mode: 'ALL_ACTIVE',
  price_display_mode: 'SHOW',
  skus: [],
  badge_label: '',
  medias: [],
  resolved_skus: activeSkus.map((s) => ({
    id: s.id,
    code: s.code || s.sku || '',
    label: skuVariationLabel(s),
    attributes: s.attributes || {},
    price: s.price,
    price_type: s.price_type,
    status: s.status,
  })),
  _productType: (product.type || 'PRODUCT').toUpperCase(),
});

export const createEmptyKit = (displayOrder) => {
  const itemId = newClientId();
  const kitId = newClientId();
  return {
    id: kitId,
    catalog_item_id: itemId,
    _itemId: itemId,
    display_name: '',
    display_description: '',
    badge_label: '',
    medias: [],
    display_order: displayOrder,
    is_available: true,
    pricing_mode: 'SUM',
    fixed_price: null,
    min_price: null,
    max_price: null,
    items: [],
  };
};

export const buildUnifiedEntries = (productItems, kits) => {
  const productEntries = (productItems || []).map((item) => ({
    rowKey: item.id,
    entryType: (item._productType || 'PRODUCT').toUpperCase() === 'SERVICE' ? 'SERVICE' : 'PRODUCT',
    display_order: item.display_order ?? 0,
    productItem: item,
    kit: null,
  }));
  const kitEntries = (kits || []).map((kit) => ({
    rowKey: kit.catalog_item_id || kit.id,
    entryType: 'KIT',
    display_order: kit.display_order ?? 1000,
    productItem: null,
    kit,
  }));
  return [...productEntries, ...kitEntries].sort((a, b) => a.display_order - b.display_order);
};

export const splitUnifiedEntries = (entries) => {
  const productItems = [];
  const kits = [];
  (entries || []).forEach((entry, idx) => {
    if (entry.entryType === 'KIT' && entry.kit) {
      kits.push({
        ...entry.kit,
        display_order: idx,
        is_available: entry.kit.is_available !== false,
      });
      return;
    }
    if (entry.productItem) {
      productItems.push({
        ...entry.productItem,
        display_order: idx,
        is_available: entry.productItem.is_available !== false,
      });
    }
  });
  return { productItems, kits };
};

export const moveUnifiedEntry = (entries, fromIndex, toIndex) => {
  if (fromIndex < 0 || toIndex < 0 || fromIndex >= entries.length || toIndex >= entries.length) {
    return entries;
  }
  const next = [...entries];
  const [removed] = next.splice(fromIndex, 1);
  next.splice(toIndex, 0, removed);
  return next.map((entry, idx) => ({ ...entry, display_order: idx }));
};

export const formatCatalogMoney = (value, locale = 'pt-BR') => {
  if (value == null || Number.isNaN(Number(value))) return null;
  return new Intl.NumberFormat(locale, { style: 'currency', currency: 'BRL' }).format(Number(value));
};

const resolveSkuDisplayPrices = (skuRow, resolvedSku) => {
  const mode = (skuRow?.price_mode || 'INHERIT').toUpperCase();
  if (mode === 'ON_REQUEST') {
    return { min: null, max: null, onRequest: true };
  }
  if (mode === 'FIXED' && skuRow.override_price != null) {
    return { min: skuRow.override_price, max: skuRow.override_price };
  }
  if (mode === 'RANGE') {
    const min = skuRow.min_price ?? resolvedSku?.price ?? 0;
    const max = skuRow.max_price ?? skuRow.min_price ?? resolvedSku?.price ?? 0;
    return { min, max };
  }
  const base = resolvedSku?.price ?? 0;
  return { min: base, max: base };
};

export const summarizeProductItemPrice = (item, locale = 'pt-BR', onRequestLabel = 'Solicitar orçamento') => {
  if ((item?.price_display_mode || 'SHOW').toUpperCase() === 'ON_REQUEST') {
    return { text: onRequestLabel, min: null, max: null, onRequest: true };
  }
  const resolved = item?.resolved_skus || [];
  const selected = (item?.sku_selection_mode || 'ALL_ACTIVE').toUpperCase() === 'SELECTED';
  const rows = selected
    ? (item.skus || []).map((sk) => {
        const rs = resolved.find((r) => r.id === sk.sku_id);
        return resolveSkuDisplayPrices(sk, rs);
      })
    : resolved.map((rs) => resolveSkuDisplayPrices({ price_mode: 'INHERIT' }, rs));

  if (rows.length === 0) return { text: '—', min: null, max: null };

  const visible = rows.filter((r) => !r.onRequest);
  if (visible.length === 0) {
    return { text: onRequestLabel, min: null, max: null, onRequest: true };
  }

  const min = Math.min(...visible.map((r) => r.min));
  const max = Math.max(...visible.map((r) => r.max));
  if (min === max) {
    const text = formatCatalogMoney(min, locale) || '—';
    return { text, min, max };
  }
  const minText = formatCatalogMoney(min, locale);
  const maxText = formatCatalogMoney(max, locale);
  return { text: `${minText} – ${maxText}`, min, max };
};

export const summarizeKitPrice = (kit, locale = 'pt-BR', sumLabel = 'Sum of items', onRequestLabel = 'Solicitar orçamento') => {
  const mode = (kit?.pricing_mode || 'SUM').toUpperCase();
  if (mode === 'ON_REQUEST') {
    return { text: onRequestLabel, min: null, max: null, onRequest: true };
  }
  if (mode === 'FIXED') {
    const text = formatCatalogMoney(kit.fixed_price, locale) || '—';
    return { text, min: kit.fixed_price, max: kit.fixed_price };
  }
  if (mode === 'RANGE') {
    const minText = formatCatalogMoney(kit.min_price, locale) || '—';
    const maxText = formatCatalogMoney(kit.max_price, locale) || '—';
    return { text: `${minText} – ${maxText}`, min: kit.min_price, max: kit.max_price };
  }
  return { text: sumLabel, min: null, max: null };
};

export const getEntryDisplayCode = (entry) => {
  if (entry.entryType === 'KIT') {
    return entry.kit?.id || entry.kit?.catalog_item_id || '';
  }
  return entry.productItem?.product_id || entry.productItem?.id || '';
};

export const getEntryDisplayName = (entry) => {
  if (entry.entryType === 'KIT') {
    return entry.kit?.display_name || '';
  }
  return entry.productItem?.display_name || '';
};

export const getEntryThumbnailUrl = (entry) => {
  const medias = entry.entryType === 'KIT' ? entry.kit?.medias : entry.productItem?.medias;
  const first = (medias || []).slice().sort((a, b) => (a.display_order ?? 0) - (b.display_order ?? 0))[0];
  return first?.file_path ? mediaFileUrl(first.file_path) : '';
};

export const normalizeHeroTag = (tag) => ({
  id: tag?.id || newClientId(),
  label: tag?.label ?? '',
  highlighted: tag?.highlighted === true || String(tag?.style || '').toUpperCase() === 'PROMO',
});

export const normalizeHeroTags = (tags) => (Array.isArray(tags) ? tags : []).map(normalizeHeroTag);

export const normalizePromoBlock = (block) => ({
  id: block?.id || newClientId(),
  tag: block?.tag ?? '',
  title: block?.title ?? '',
  description: block?.description ?? '',
  block_type: block?.block_type ?? '',
  media_id: block?.media_id ?? '',
});

export const normalizePromoBlocks = (blocks) => (Array.isArray(blocks) ? blocks : []).map(normalizePromoBlock);

export const newHeroTag = () => ({ id: newClientId(), label: '', highlighted: false });

export const newPromoBlock = () => ({
  id: newClientId(),
  tag: '',
  title: '',
  description: '',
  block_type: '',
  media_id: '',
});
